import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Select from 'react-select';
import ActionButton from '../../components/ui/ActionButton';
import CopyButton from '../../components/ui/CopyButton';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import { isSystemCategory } from '../../utils/categoryUtils';
import { categoryDetailAPI, dashboardAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isMobileRefsCategory as checkMobileRefs, isVerifBcCategory as checkVerifBc, useMobileCaisseNames } from './transactionRefRules';
import addIcon from '../../assets/icons/add.svg';
import trashIcon from '../../assets/icons/trash.svg';

const DOC_TYPE_OPTIONS = [
  { value: 'BC', label: 'Bon de caisse' }
];

// Exact select styles from CreationModal
const getSelectStyles = (hasError, hasValue) => ({
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#ffffff',
    border: hasError ? '2px solid #EF456E' : (state.isFocused || hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3'),
    borderRadius: '8px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    paddingLeft: '12px',
    paddingRight: '12px',
    boxShadow: 'none',
    '&:hover': {
      border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    height: '48px',
    minHeight: '48px',
    overflow: 'hidden',
    flex: 1,
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#000000',
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    color: '#A3A3A3',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#38485C',
    padding: '0',
    minWidth: '20px',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    marginTop: '4px',
    zIndex: 999999,
    overflow: 'hidden',
  }),
  menuList: (provided) => ({ ...provided, padding: '0' }),
  option: (provided, state) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: state.isSelected ? '#F0F8FF' : state.isFocused ? '#F3F4F6' : '#ffffff',
    color: state.isSelected ? '#027BC6' : '#333',
    fontSize: '16px',
    fontWeight: state.isSelected ? 700 : 600,
    fontFamily: 'Montserrat, sans-serif',
    cursor: 'pointer',
    borderBottom: '1px solid #E4E7EA',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: state.isSelected ? '#E6F3FF' : '#F3F4F6' },
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 999999 }),
});

// Exact TextField sx from CreationModal (text/number fields)
const getFieldSx = (hasError, hasValue) => ({
  '& .MuiOutlinedInput-root': {
    height: '48px',
    paddingLeft: '12px',
    paddingRight: '12px',
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    backgroundColor: '#ffffff',
    border: hasError ? '2px solid #EF456E' : (hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3'),
    borderRadius: '8px',
    '&:hover': { border: hasError ? '2px solid #EF456E' : '1px solid #007BC6' },
    '&.Mui-focused': { border: hasError ? '2px solid #EF456E' : '1px solid #007BC6' },
    '& fieldset': { border: 'none' },
  },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    '&::placeholder': { color: '#A3A3A3', opacity: 1 },
  },
  '& .MuiFormHelperText-root': { display: 'none' },
});

// Exact textarea sx from CreationModal
const textareaSx = (hasError, hasValue) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    border: hasError ? '2px solid #EF456E' : (hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3'),
    borderRadius: '12px',
    padding: '0',
    height: 'auto',
    minHeight: '72px',
    maxHeight: '120px',
    overflow: 'auto',
    '&:hover': { border: hasError ? '2px solid #EF456E' : '1px solid #007BC6' },
    '&.Mui-focused': { border: hasError ? '2px solid #EF456E' : '1px solid #007BC6' },
    '& fieldset': { border: 'none' },
  },
  '& .MuiInputBase-input': {
    width: '100%',
    minHeight: '48px',
    maxHeight: '96px',
    padding: '12px',
    border: 'none',
    outline: 'none',
    borderRadius: '12px',
    fontFamily: 'Montserrat',
    fontSize: '16px',
    fontWeight: 600,
    color: '#000000',
    backgroundColor: 'transparent',
    resize: 'none',
    boxSizing: 'border-box',
    overflow: 'auto',
    '&::placeholder': { color: '#A3A3A3', opacity: 1 },
  },
  '& .MuiInputLabel-root': { display: 'none' },
});

const ErrorText = ({ msg }) => (
  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#EF456E', mt: '4px', ml: '4px' }}>
    {msg}
  </Typography>
);

const TransactionCreationModal = ({ open, onClose, onSubmit, loading = false, caisse = null }) => {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [amount, setAmount] = useState('');
  const [codeClient, setCodeClient] = useState('');
  const [documents, setDocuments] = useState([{ type: DOC_TYPE_OPTIONS[0], value: '', touched: false }]);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [caisseSolde, setCaisseSolde] = useState(null);
  const { user } = useAuth();

  const resetForm = useCallback(() => {
    setCategory(null);
    setAmount('');
    setCodeClient('');
    setDocuments([{ type: DOC_TYPE_OPTIONS[0], value: '', touched: false }]);
    setDescription('');
    setErrors({});
    setCaisseSolde(null);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    const isVersement = (category?.data?.coffreFactor ?? 0) > 0;
    if (!isVersement) { setCaisseSolde(null); return; }
    const caisseId = caisse?.id
      || user?.assignedCaisses?.[0]?.caisse?.id
      || user?.assignedCaisses?.[0]?.id;
    if (!caisseId) return;
    dashboardAPI.getCaisseDashboard(caisseId)
      .then(res => setCaisseSolde(res.data?.caisseBalance ?? null))
      .catch(() => {});
  }, [category, caisse, user]);

  const fetchCategories = useCallback(async () => {
    try {
      let response;
      if (user?.role === 'AGENT' && user?.assignedCaisses?.length > 0) {
        const caisseId = user.assignedCaisses[0]?.caisse?.id || user.assignedCaisses[0]?.id;
        response = await categoryDetailAPI.getForFiltersByCaisse(caisseId);
      } else if (caisse?.id) {
        response = await categoryDetailAPI.getForFiltersByCaisse(caisse.id);
      } else {
        response = await categoryDetailAPI.getAll();
      }
      const data = response?.data || response;
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    }
  }, [user, caisse]);

  useEffect(() => {
    if (open) fetchCategories();
  }, [open, fetchCategories]);

  const mobileCaisseNames = useMobileCaisseNames(caisse?.name);

  const categoryOptions = useMemo(() =>
    categories
      .filter(cat => !isSystemCategory(cat))
      .map(cat => ({ value: cat.id, label: cat.category?.label || cat.label, data: cat })),
    [categories]
  );

  const caisseType = caisse?.type || category?.data?.caisse?.type;
  const isMobileRefsCategory = checkMobileRefs(caisseType, category?.label);
  const isVersementCoffre = (category?.data?.coffreFactor ?? 0) > 0;
  const isVerifBcCategory = checkVerifBc(caisse?.name, category?.label, mobileCaisseNames);
  const showCodeClient = isMobileRefsCategory;
  const showDocSection = isMobileRefsCategory || isVerifBcCategory;
  const showAddDocButton = false;

  // Reset reference fields when category changes
  useEffect(() => {
    setCodeClient('');
    setDocuments([{ type: DOC_TYPE_OPTIONS[0], value: '', touched: false }]);
  }, [category]);

  const addDocument = () => setDocuments(prev => [...prev, { type: DOC_TYPE_OPTIONS[0], value: '', touched: false }]);
  const removeDocument = (index) => setDocuments(prev => prev.filter((_, i) => i !== index));
  const updateDocValue = (index, val) =>
    setDocuments(prev => prev.map((d, i) => i === index ? { ...d, value: val } : d));
  const touchDocType = (index) =>
    setDocuments(prev => prev.map((d, i) => i === index ? { ...d, touched: true } : d));

  const validate = () => {
    const newErrors = {};
    if (!category) newErrors.category = 'Le champ est requis';
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) newErrors.amount = 'Le montant doit être un nombre positif';
    if (!showDocSection && !description.trim()) newErrors.description = 'Le champ est requis';
    if (showCodeClient && !codeClient.trim()) newErrors.codeClient = 'Le champ est requis';
    if (showDocSection && !documents[0]?.value.trim()) newErrors.bc = 'Le champ est requis';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    let caisseId = null;
    if (user?.role === 'AGENT' && user?.assignedCaisses?.length > 0) {
      caisseId = user.assignedCaisses[0]?.caisse?.id || user.assignedCaisses[0]?.id;
    }

    const references = [];
    if (showCodeClient && codeClient.trim()) references.push({ type: 'CODE_CLIENT', value: codeClient.trim() });
    if (showDocSection) {
      documents.forEach(doc => {
        if (doc.value.trim()) references.push({ type: doc.type.value, value: doc.value.trim() });
      });
    }

    onSubmit({
      caisseId,
      categoryDetailId: category.value,
      categoryName: category.label,
      categoryData: category.data,
      amount: parseFloat(amount),
      description: description.trim(),
      references: references.length > 0 ? references : null
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      slotProps={{ root: { sx: { zIndex: 1600 } } }}
      PaperProps={{
        sx: {
          background: '#F9FAFC',
          boxShadow: '0px 20px 80px #00000011',
          borderRadius: '12px',
          width: '800px',
          maxWidth: 'none',
          maxHeight: 'none',
          overflowY: 'visible',
          margin: 0,
        }
      }}
      sx={{
        zIndex: 1600,
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '20vh',
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pt: 3, px: 5 }}>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: 600, color: '#36495C', textTransform: 'none', lineHeight: 1 }}>
            Créer une transaction
          </Typography>
          <CloseIcon onClick={onClose} sx={{ cursor: 'pointer', color: '#36495C', fontSize: '24px', lineHeight: 1 }} />
        </Box>

        {/* Form body */}
        <Box sx={{ px: 5, pb: 2 }}>

          {/* Row 1: Category + Amount */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Select
                value={category}
                onChange={(opt) => { setCategory(opt); setErrors(prev => ({ ...prev, category: '' })); }}
                options={categoryOptions}
                placeholder="Catégorie"
                menuPortalTarget={document.body}
                styles={getSelectStyles(!!errors.category, !!category)}
                autoFocus
              />
              {errors.category && <ErrorText msg={errors.category} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setErrors(prev => ({ ...prev, amount: '' })); }}
                placeholder="Montant"
                type="number"
                fullWidth
                sx={getFieldSx(!!errors.amount, !!amount)}
                inputProps={{ min: 0, step: 'any' }}
              />
              {errors.amount && <ErrorText msg={errors.amount} />}
              {isVersementCoffre && caisseSolde != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '4px' }}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: '#919191', lineHeight: 1.4 }}>
                    {`Solde caisse : ${formatAmountForDisplay(caisseSolde, false).text}`}
                  </Typography>
                  <CopyButton value={String(caisseSolde)} />
                </Box>
              )}
            </Box>
          </Box>

          {/* Code Client — mobile règlement categories only */}
          {showCodeClient && (
            <Box sx={{ mb: 2 }}>
              <TextField
                value={codeClient}
                onChange={(e) => { setCodeClient(e.target.value); setErrors(prev => ({ ...prev, codeClient: '' })); }}
                placeholder="Code client"
                fullWidth
                sx={getFieldSx(!!errors.codeClient, !!codeClient)}
              />
              {errors.codeClient && <ErrorText msg={errors.codeClient} />}
            </Box>
          )}

          {/* Document rows — mobile règlement + vérification only */}
          {showDocSection && documents.length > 0 && (
            <Box
              sx={{
                background: '#E3E3E33C',
                border: '1px solid #E3E3E3',
                borderRadius: '12px',
                px: 2,
                py: 2,
                mb: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: '300px',
                overflowY: 'auto'
              }}
            >
              {documents.map((doc, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={doc.type}
                      onChange={(opt) => { touchDocType(index); setDocuments(prev => prev.map((d, i) => i === index ? { ...d, type: opt } : d)); }}
                      options={DOC_TYPE_OPTIONS}
                      menuPortalTarget={document.body}
                      styles={getSelectStyles(false, doc.touched && !!doc.type)}
                      isDisabled={isMobileRefsCategory || isVerifBcCategory}
                      components={isMobileRefsCategory || isVerifBcCategory ? { DropdownIndicator: () => null, IndicatorSeparator: () => null } : undefined}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      value={doc.value}
                      onChange={(e) => { const val = doc.type.value === 'BC' ? e.target.value.replace(/\D/g, '') : e.target.value; updateDocValue(index, val); if (index === 0) setErrors(prev => ({ ...prev, bc: '' })); }}
                      placeholder={isVerifBcCategory ? 'N° bon de caisse' : 'Numéro de document'}
                      fullWidth
                      inputProps={doc.type.value === 'BC' ? { inputMode: 'numeric' } : {}}
                      sx={getFieldSx(!!errors.bc && index === 0, !!doc.value)}
                    />
                    {errors.bc && index === 0 && <ErrorText msg={errors.bc} />}
                  </Box>
                  {!isMobileRefsCategory && !isVerifBcCategory && (
                    <Box
                      onClick={() => removeDocument(index)}
                      sx={{ width: '16px', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { opacity: 0.7 } }}
                    >
                      <img src={trashIcon} alt="Supprimer" style={{ width: '16px', height: '16px' }} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {/* Add document — mobile règlement only, not vérification */}
          {showAddDocButton && (
            <Box
              onClick={addDocument}
              sx={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', mb: 1, width: 'fit-content', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
            >
              <img
                src={addIcon}
                alt="Ajouter"
                style={{ width: '18px', height: '18px', filter: 'brightness(0) saturate(100%) invert(28%) sepia(96%) saturate(710%) hue-rotate(170deg) brightness(100%) contrast(101%)' }}
              />
              <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#027BC6' }}>
                Ajouter un document
              </Typography>
            </Box>
          )}

          {/* Description */}
          <Box>
            <TextField
              value={description}
              onChange={(e) => { setDescription(e.target.value); setErrors(prev => ({ ...prev, description: '' })); }}
              placeholder="Description"
              fullWidth
              multiline
              rows={3}
              sx={textareaSx(!!errors.description, !!description.trim())}
            />
            {errors.description && <ErrorText msg={errors.description} />}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', px: 5, pb: 3, flexShrink: 0 }}>
          <ActionButton
            variant="text"
            onClick={onClose}
            sx={{ color: '#36495C', fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, textTransform: 'none', minWidth: 'auto', px: 2 }}
          >
            Annuler
          </ActionButton>
          <ActionButton
            variant="contained"
            onClick={handleSubmit}
            loading={loading}
            sx={{
              width: '318px',
              height: '48px',
              backgroundColor: '#027BC6',
              boxShadow: '0px 4px 10px #BEBEBE50',
              borderRadius: '12px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#025a9c' }
            }}
          >
            Créer une transaction
          </ActionButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionCreationModal;
